import React, { useState, useEffect, useRef } from 'react';
import styles from './TimelineSection.module.scss';
import { motion, AnimatePresence } from 'framer-motion';

const timeline = [
  { 
    year: '2020 - 2021', 
    title: 'Early Exploration', 
    desc: 'Early experiments in RWA tokenisation platforms, start gaining traction.',
    icon: (
      <svg className={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM11 7H13V9H11V7ZM11 11H13V17H11V11Z" fill="currentColor" />
      </svg>
    )
  },
  { 
    year: '2023', 
    title: 'BlackRock Endorsement',
    desc: '"Tokenization of RWAs could make capital markets more efficient" — CEO of BlackRock',
    icon: (
      <svg className={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 7V3H2V21H22V7H12ZM6 19H4V17H6V19ZM6 15H4V13H6V15ZM6 11H4V9H6V11ZM6 7H4V5H6V7ZM10 19H8V17H10V19ZM10 15H8V13H10V15ZM10 11H8V9H10V11ZM10 7H8V5H10V7ZM20 19H12V17H14V15H12V13H14V11H12V9H20V19ZM18 11H16V13H18V11ZM18 15H16V17H18V15Z" fill="currentColor" />
      </svg>
    )
  },
  { 
    year: 'Early 2024', 
    title: 'BUIDL Fund Launch',
    desc: 'BlackRock launches BUIDL Fund, $375 million market cap',
    icon: (
      <svg className={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 9.2H8V19H5V9.2ZM10.6 5H13.4V19H10.6V5ZM16.2 13H19V19H16.2V13Z" fill="currentColor" />
      </svg>
    )
  },
  { 
    year: 'Mid 2024', 
    title: 'Major Investment',
    desc: 'BlackRock leads $47M investment in Securitize, specializing in RWA tokenization',
    icon: (
      <svg className={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.8 10.9C9.53 10.31 8.8 9.7 8.8 8.75C8.8 7.66 9.81 6.9 11.5 6.9C13.28 6.9 13.94 7.75 14 9H16.21C16.14 7.28 15.09 5.7 13 5.19V3H10V5.16C8.06 5.58 6.5 6.84 6.5 8.77C6.5 11.08 8.41 12.23 11.2 12.9C13.7 13.5 14.2 14.38 14.2 15.31C14.2 16 13.71 17.1 11.5 17.1C9.44 17.1 8.63 16.18 8.5 15H6.32C6.44 17.19 8.08 18.42 10 18.83V21H13V18.85C14.95 18.48 16.5 17.35 16.5 15.3C16.5 12.46 14.07 11.49 11.8 10.9Z" fill="currentColor" />
      </svg>
    )
  },
  { 
    year: '2030', 
    title: 'Market Projection',
    desc: 'RWA tokenisation: 10% of global assets, est. market value $16 trillion',
    icon: (
      <svg className={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3.5 18.49L9.5 12.48L13.5 16.48L22 6.92L20.59 5.51L13.5 13.48L9.5 9.48L2 16.99L3.5 18.49Z" fill="currentColor" />
      </svg>
    )
  },
];

const TimelineSection = () => {
  const [activeItem, setActiveItem] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  const timelineLength = timeline.length;
  
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !containerRef.current) return;
      
      // Get the section's position
      const sectionRect = sectionRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Calculate when section reaches the top of the viewport
      const sectionTopReachesViewportTop = sectionRect.top <= 0;
      
      // Calculate when we've scrolled to the end of the timeline
      // (number of items * viewport height)
      const timelineScrollDistance = timelineLength * viewportHeight;
      
      // Calculate scroll progress relative to section
      const relativeScrollProgress = Math.abs(sectionRect.top) / timelineScrollDistance;
      
      // Only keep fixed until we reach the last item
      const shouldBeFixed = sectionTopReachesViewportTop && relativeScrollProgress <= 0.9;
      
      // Check if we're at the last item and near its end
      const atLastItemEnd = relativeScrollProgress > 0.8 && relativeScrollProgress <= 0.9;
      
      // Check if we've completed the timeline
      const hasCompletedTimeline = relativeScrollProgress > 0.9;
      
      setIsComplete(hasCompletedTimeline);
      
      // Apply the proper positioning
      if (shouldBeFixed) {
        // Section is at or above viewport top, but we haven't scrolled past timeline
        // Make container fixed
        containerRef.current.style.position = 'fixed';
        containerRef.current.style.top = '0';
        containerRef.current.style.visibility = 'visible';
        
        // Calculate progress within the timeline (0 to 1)
        const scrollProgress = Math.min(relativeScrollProgress / 0.9, 1);
        
        // Update progress indicator
        if (progressRef.current) {
          progressRef.current.style.setProperty('--progress', `${scrollProgress * 100}%`);
        }
        
        // Determine active timeline item based on progress
        const itemIndex = Math.min(
          Math.floor(scrollProgress * timelineLength),
          timelineLength - 1
        );
        
        // Update active item
        setActiveItem(itemIndex);
        
        // If at last item and approaching the end, start fading out
        if (atLastItemEnd) {
          const fadeOutProgress = (relativeScrollProgress - 0.8) / 0.1;
          containerRef.current.style.opacity = (1 - fadeOutProgress).toString();
        } else {
          containerRef.current.style.opacity = '1';
        }
        
      } else if (hasCompletedTimeline) {
        // We've scrolled past the timeline, make it completely invisible
        containerRef.current.style.visibility = 'hidden';
        containerRef.current.style.position = 'absolute';
        containerRef.current.style.top = `${timelineScrollDistance}px`;
        containerRef.current.style.opacity = '0';
        containerRef.current.style.pointerEvents = 'none';
      } else {
        // Section hasn't reached the top yet, keep container at the top of section
        containerRef.current.style.position = 'absolute';
        containerRef.current.style.top = '0';
        containerRef.current.style.opacity = '1';
        containerRef.current.style.visibility = 'visible';
        containerRef.current.style.pointerEvents = 'auto';
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    // Initial calculation
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [timelineLength]);
  
  // Handle manual navigation via click
  const handleItemClick = (index) => {
    if (index === activeItem) return;
    
    setActiveItem(index);
    
    // Calculate scroll position for this timeline item
    if (sectionRef.current) {
      const sectionTop = window.pageYOffset + sectionRef.current.getBoundingClientRect().top;
      const viewportHeight = window.innerHeight;
      
      // Calculate the target scroll position
      // We want to scroll to section top (to make it sticky) 
      // plus the offset for the specific timeline item
      const targetScroll = sectionTop + (index * viewportHeight);
      
      window.scrollTo({
        top: targetScroll,
        behavior: 'smooth'
      });
    }
  };
  
  return (
    <section className={styles.timelineSectionParallax} ref={sectionRef}>
      <div 
        className={`${styles.timelineContainer} ${isComplete ? styles.completed : ''}`}
        ref={containerRef}
      >
        <h2 className={styles.title}>Market Timeline</h2>
        
        <div className={styles.timelineHeader}>
          <div className={styles.timelinePoints}>
            <div className={styles.horizontalLine}></div>
            {timeline.map((item, idx) => (
              <div 
                key={idx} 
                className={`${styles.timelinePoint} ${activeItem === idx ? styles.activePoint : ''}`}
                onClick={() => handleItemClick(idx)}
              >
                <motion.div
                  className={styles.timelinePointDot}
                  animate={{
                    scale: activeItem === idx ? 1.2 : 1,
                    backgroundColor: activeItem === idx ? '#00A6BB' : 'rgba(255, 255, 255, 0.2)'
                  }}
                  transition={{ duration: 0.3 }}
                ></motion.div>
                <div className={styles.yearLabel}>{item.year}</div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.cardControls}>
          <AnimatePresence mode="wait">
            <motion.div 
              className={styles.activeCardContainer}
              key={activeItem}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <div className={styles.activeCard}>
                <div className={styles.iconContainer}>
                  {timeline[activeItem].icon}
                </div>
                <h3 className={styles.itemTitle}>{timeline[activeItem].title}</h3>
                <p>{timeline[activeItem].desc}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        
        <div className={styles.timelineNavigation}>
          {timeline.map((_, idx) => (
            <motion.button
              key={idx}
              className={styles.navDot}
              animate={{
                scale: activeItem === idx ? 1.2 : 1,
                backgroundColor: activeItem === idx ? '#00A6BB' : 'rgba(255, 255, 255, 0.2)'
              }}
              onClick={() => handleItemClick(idx)}
              aria-label={`Timeline item ${idx + 1}`}
            />
          ))}
        </div>
        
        <div className={styles.progressIndicator} ref={progressRef}></div>
      </div>
    </section>
  );
};

export default TimelineSection;
